<template>
  <!-- eslint-disable  max-len -->
  <div
    id="ping-authenticator"
    class="configuration"
  >
    <template v-if="isActivateMode">
      <div class="form-container">
        <label class="form-label">Authenticator Name</label>
        <input
          id="name-input-ping"
          v-model="authenticatorName"
          class="form-field"
          type="text"
          maxlength="50"
        />
      </div>
      <div class="form-container">
        <div class="form-label-container">
          <label class="form-label">PingID Properties File</label>
          <div class="form__error">
            {{ errorMessage }}
          </div>
        </div>
        <textarea
          id="ping-properties-input"
          v-model="pingProperties"
          v-focus
          class="form-field form-field--textarea"
          placeholder="Copy the contents of your PingID Properties file here."
          rows="9"
        >
        </textarea>
      </div>
      <div class="mb-1">
        The default authenticator will handle 2FA account-wide.
      </div>
      <label
        class="checkbox mb-1 ml-1"
        for="default-authenticator-check"
      >
        <input
          id="default-authenticator-check"
          v-model="defaultAuthenticatorCheck"
          type="checkbox"
        />
        <span>Set as Default Authenticator</span>
      </label>
      <div class="configuration-footer">
        <a
          :href="pingUrl"
          target="_blank"
        > Where do I find my PingID Properties File? </a>
        <div class="configuration-footer-actions">
          <button
            id="cancel-edit-button-ping"
            class="btn btn--grey mr-2"
            @click="emitCancel"
          >
            Cancel
          </button>
          <button
            id="submit-edit-button-ping"
            class="btn"
            @click="emitCreateCredential"
          >
            Activate PING
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="flex-grid">
        <div class="column--two">
          <div class="configuration-content__heading">
            Ping is available to handle second-factor authentication.
          </div>
          <div class="configuration-content__description">
            To check if Ping is working correctly, go to the User Management page, and select ‘Send
            Test Request’ on a user to send them an authentication request. They must be an active
            user in PingID.
          </div>
          <button
            id="ping-deactivate-button"
            class="btn btn__text btn__text--red mt-2"
            @click="deleteConfiguration"
          >
            Delete Authenticator
          </button>
        </div>
        <div class="column--two">
          <div class="mb-1">
            The default authenticator will handle 2FA account-wide.
          </div>
          <label
            class="checkbox mb-1 ml-1 mt-2"
            for="default-authenticator-check"
          >
            <input
              id="default-authenticator-check"
              v-model="defaultAuthenticatorCheck"
              type="checkbox"
            />
            <span>Set as Default Authenticator</span>
          </label>
          <div class="mt-2">
            <button
              id="cancel-edit-button-ping"
              class="btn btn--grey mr-2"
              @click="emitCancel"
            >
              Cancel
            </button>
            <button
              id="submit-edit-button-ping"
              class="btn"
              :disabled="!isDefault && !defaultAuthenticatorCheck"
              @click="emitUpdate"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import AuthenticatorMixin from '@/mixins/authenticatorMixin'
export default {
  name: 'PingConfiguration',
  mixins: [AuthenticatorMixin],
  props: [
    'selectedAuthenticator',
    'defaultAuthenticator',
    'isActivateMode',
    'parentError',
    'loading',
  ],
  data() {
    return {
      errorMessage: '',
      defaultAuthenticatorCheck: false,
      pingProperties: null,
      authenticatorName: '',
      pingUrl:
        'https://apidocs.pingidentity.com/pingid-api/guide/pingid-api/pid_c_PingIDapiOverview/#The-PingID-Properties-File',
    }
  },
  computed: {
    isDefault() {
      return this.isDefaultAuthenticator(this.defaultAuthenticator, this.AUTH_TYPES.PING)
    },
  },
  created() {
    this.defaultAuthenticatorCheck = this.isDefault
  },
  methods: {
    pingPropertiesToObject() {
      const _pingPropertiesArray = this.pingProperties.split('\n')
      _pingPropertiesArray.splice(0, 2)
      const _pingPropertiesObject = _pingPropertiesArray.reduce((newObject, string) => {
        const _formatValue = string.replace(/=(?=.[^=])/g, ': ')
        const _keyValuePair = _formatValue.split(': ')
        const [key, value] = _keyValuePair
        newObject[key] = value
        return newObject
      }, {})
      return _pingPropertiesObject
    },
    /*
     * Check if user is setting the authenticator to be the default
     * or if the authenticator is already the default
     * authenticator, then set to none. If update button will be disabled if
     * this is not the default authenticator or the checkbox is not checked.
     */
    emitUpdate() {
      const _accountSettingPayload = this.generateSettingsPayload(
        this.defaultAuthenticatorCheck,
        this.AUTH_TYPES.PING,
        this.selectedAuthenticator.uuid,
        this.defaultAuthenticator.bcidSettings,
      )
      this.$emit('update-setting', _accountSettingPayload)
    },
    emitCreateCredential() {
      this.errorMessage = ''
      if (this.pingProperties) {
        const _pingCredentials = this.pingPropertiesToObject()
        const _credentialPayload = {
          setDefault: this.defaultAuthenticatorCheck,
          credential: {
            name: this.authenticatorName || 'PingID Credential',
            description: 'This is the PingID credential.',
            url: 'www.ping.com',
            type: this.AUTH_TYPES.PING,
            rawData: _pingCredentials,
          },
        }
        this.$emit('create-credential', _credentialPayload)
      } else {
        this.errorMessage = 'Please provide the contents of your PingID Properties File.'
      }
    },
    deleteConfiguration() {
      this.$emit('deactivate')
    },
    emitCancel() {
      this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.configuration {
  width: 100%;
  &-content {
    @include flex-display();
    @include flex-direction(column);
    width: 50%;
    &__heading {
      font-weight: 600;
      margin-bottom: 1em;
    }
    &__description {
      color: $grey-text;
      line-height: 1.5em;
    }
  }
  &-footer {
    width: 100%;
    @include flex-display();
    @include flex-justify-content(space-between);
    @include flex-align-items(center);
    margin-top: 2em;
    &__error {
      color: $red;
    }
  }
}
</style>
